import { useMemo, useState } from 'react';
import { Pressable, Text, View, Modal, FlatList, type StyleProp, type ViewStyle } from 'react-native';
import { Check, ChevronDown } from 'lucide-react-native';

import { colors, radius, typography } from '@/theme/tokens';

export interface SelectOption {
  value: string;
  label: string;
  hint?: string;
}

interface Props {
  label?: string;
  value: string | null;
  options: SelectOption[];
  onChange: (value: string | null) => void;
  placeholder?: string;
  containerStyle?: StyleProp<ViewStyle>;
  allowClear?: boolean;
  error?: string | null;
}

export function Select({ label, value, options, onChange, placeholder = 'Seleccionar…', containerStyle, allowClear, error }: Props) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => options.find((o) => o.value === value) ?? null, [options, value]);

  return (
    <View style={[{ gap: 6 }, containerStyle]}>
      {label ? (
        <Text style={{ fontSize: 13, fontWeight: typography.weights.semibold, color: colors.ink[700], letterSpacing: 0.1 }}>
          {label}
        </Text>
      ) : null}

      <Pressable
        onPress={() => setOpen(true)}
        style={({ hovered }) => ({
          flexDirection: 'row',
          alignItems: 'center',
          gap: 10,
          paddingHorizontal: 14,
          height: 50,
          borderWidth: 1.5,
          borderColor: error ? colors.danger[500] : hovered ? colors.magenta[300] : colors.ink[200],
          borderRadius: radius.xl,
          backgroundColor: colors.white,
          // @ts-ignore web only
          cursor: 'pointer',
          // @ts-ignore web only
          transitionProperty: 'border-color',
          // @ts-ignore web only
          transitionDuration: '160ms',
        })}
      >
        <Text
          style={{
            flex: 1,
            color: selected ? colors.ink[900] : colors.ink[400],
            fontSize: typography.sizes.md,
            fontFamily: typography.body.fontFamily,
          }}
          numberOfLines={1}
        >
          {selected?.label ?? placeholder}
        </Text>
        <ChevronDown size={16} color={colors.ink[400]} />
      </Pressable>

      {error ? <Text style={{ fontSize: 12, color: colors.danger[600], fontWeight: typography.weights.semibold }}>{error}</Text> : null}

      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable
          style={{ flex: 1, backgroundColor: 'rgba(15,23,42,0.45)', justifyContent: 'center', alignItems: 'center' }}
          onPress={() => setOpen(false)}
        >
          <Pressable
            onPress={(e) => e.stopPropagation()}
            style={{
              width: '92%',
              maxWidth: 480,
              maxHeight: '70%',
              backgroundColor: colors.white,
              borderRadius: radius['3xl'],
              padding: 12,
              // @ts-ignore web only
              boxShadow: '0 24px 48px -12px rgba(15, 23, 42, 0.35)',
            }}
          >
            <Text style={{ paddingHorizontal: 12, paddingTop: 8, paddingBottom: 12, fontSize: 12, color: colors.ink[400], textTransform: 'uppercase', letterSpacing: 1.2, fontWeight: typography.weights.bold }}>
              {label ?? 'Opciones'}
            </Text>
            {allowClear && value ? (
              <Pressable
                onPress={() => {
                  onChange(null);
                  setOpen(false);
                }}
                style={({ hovered }) => ({
                  paddingHorizontal: 12,
                  paddingVertical: 12,
                  borderRadius: 12,
                  backgroundColor: hovered ? colors.crema[50] : 'transparent',
                  // @ts-ignore web only
                  cursor: 'pointer',
                })}
              >
                <Text style={{ color: colors.ink[500], fontSize: 14 }}>— Quitar selección —</Text>
              </Pressable>
            ) : null}
            <FlatList
              data={options}
              keyExtractor={(o) => o.value}
              renderItem={({ item }) => {
                const active = item.value === value;
                return (
                  <Pressable
                    onPress={() => {
                      onChange(item.value);
                      setOpen(false);
                    }}
                    style={({ hovered }) => ({
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 10,
                      paddingHorizontal: 12,
                      paddingVertical: 12,
                      borderRadius: 12,
                      backgroundColor: active ? colors.magenta[50] : hovered ? colors.crema[50] : 'transparent',
                      // @ts-ignore web only
                      cursor: 'pointer',
                    })}
                  >
                    <View style={{ flex: 1 }}>
                      <Text style={{ color: active ? colors.magenta[800] : colors.ink[900], fontWeight: active ? typography.weights.bold : typography.weights.medium, fontSize: 14 }}>
                        {item.label}
                      </Text>
                      {item.hint ? <Text style={{ color: colors.ink[500], fontSize: 12, marginTop: 2 }}>{item.hint}</Text> : null}
                    </View>
                    {active ? <Check size={16} color={colors.magenta[700]} /> : null}
                  </Pressable>
                );
              }}
            />
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}
